import React from "react";
import { getCampusText } from "../../../../utils/LinkedProgrammeUtils";
import {
    StyledIssueArrow,
    StyledIssueCode,
    StyledIssueContent,
    StyledIssueDescription,
    StyledIssueDivider,
    StyledIssueIcon,
    StyledIssueMain,
    StyledIssueModuleName,
    StyledIssueRow,
    StyledMismatchTag,
    StyledMismatchTags,
    StyledPopoverIssues,
} from "./ModuleReadinessPopover.styles";

/*
 * Matchstatus meanings (set by PmcController.js, see the comment there):
 * 0 = no course anywhere has this NetSuite id
 * 2 = intake/campus/type mismatch
 * 3 = matched but not linked to this version's modules
 * 4 = matched and linked but missing from the study path
 */
const MATCH_STATUS_TONE = {
    0: { tone: "destructive", icon: "times-circle" },
    3: { tone: "muted", icon: "minus-circle" },
    4: { tone: "info", icon: "info-circle" },
};
const DEFAULT_MATCH_STATUS_TONE = { tone: "warning", icon: "exclamation-circle" };

/**
 * Module-readiness problem rows shown inside ModuleReadinessPopover.
 * @param {Array<object>} problems Readiness problems to render.
 */
function ModuleReadinessIssuesList({ problems }) {
    return (
        <StyledPopoverIssues>
            {problems.map((problem, index) => {
                const mismatchCampus = problem.mismatchCampus || problem.campusMismatch;

                const mismatchTags = [
                    problem.mismatchIntake || problem.intakeMismatch,
                    mismatchCampus && getCampusText(mismatchCampus),
                    problem.mismatchType || problem.typeMismatch,
                ].filter(Boolean);

                const missingVersionModule = problem.matchStatus === 3;
                // Per design: status 4 rows show only the linked code, status 3 rows only the module code
                const showModuleCode = problem.matchStatus !== 4 && problem.moduleCode;
                const showLinkedCode = !missingVersionModule && problem.linkedCode;

                const { tone, icon } =
                    MATCH_STATUS_TONE[problem.matchStatus] || DEFAULT_MATCH_STATUS_TONE;

                return (
                    <StyledIssueRow
                        key={`${problem.moduleCode}-${problem.linkedCode}-${index}`}
                    >
                        <StyledIssueIcon
                            $tone={tone}
                            className={`fas fa-${icon}`}
                            aria-hidden="true"
                        />

                        <StyledIssueContent>
                            <StyledIssueMain>
                                <StyledIssueModuleName>
                                    {problem.moduleName || "Module"}
                                </StyledIssueModuleName>

                                {showModuleCode && (
                                    <>
                                        <StyledIssueDivider />
                                        <StyledIssueCode $destructive={missingVersionModule}>
                                            {problem.moduleCode}
                                        </StyledIssueCode>
                                    </>
                                )}

                                {showLinkedCode && (
                                    <>
                                        <StyledIssueArrow>→</StyledIssueArrow>
                                        <StyledIssueCode>{problem.linkedCode}</StyledIssueCode>
                                    </>
                                )}
                            </StyledIssueMain>

                            {mismatchTags.length > 0 && (
                                <StyledMismatchTags>
                                    {mismatchTags.map((mismatchValue, mismatchIndex) => (
                                        <StyledMismatchTag key={mismatchIndex}>
                                            {mismatchValue}
                                            <i className="fas fa-not-equal" aria-hidden="true" />
                                        </StyledMismatchTag>
                                    ))}
                                </StyledMismatchTags>
                            )}

                            {problem.explanation && (
                                <StyledIssueDescription>
                                    {problem.explanation}
                                </StyledIssueDescription>
                            )}
                        </StyledIssueContent>
                    </StyledIssueRow>
                );
            })}
        </StyledPopoverIssues>
    );
}

export default ModuleReadinessIssuesList;
